// src/portal/PortalRevisionRail.jsx
//
// PORTED from the mock's `.revisions` — a horizontal timeline of revision
// cards joined by dashed connectors.
//
// ── WHAT HAPPENS AT EIGHT REVISIONS ────────────────────────────────────────
//
// The mock draws three, which is what fits. Revisions accumulate one per cycle,
// so the question is what the rail does when it stops fitting, and it is
// answered here rather than discovered later.
//
// IT SCROLLS, ANCHORED TO THE END. The track is overflow-x:auto and is scrolled
// fully right on mount and whenever the revision count changes, so the CURRENT
// revision -- the only one anybody can act on -- is the one on screen, and R1
// leaves to the left.
//
// The alternative was collapsing the middle to "R1 … R7 R8". That hides history
// behind an interaction in the one panel whose entire job is showing history,
// and it makes the common case (three or fewer) carry machinery for the rare
// one. A timeline that scrolls is what a timeline does.
import { useEffect, useRef } from "react";
import { revisionRail } from "./portalRevisions";

export default function PortalRevisionRail({ revisions, markup, currentRevision, status }) {
  const trackRef = useRef(null);
  const entries = revisionRail({ revisions, markup, currentRevision, status });

  useEffect(() => {
    const el = trackRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [entries.length]);

  if (entries.length === 0) return null;

  return (
    <section className="flex flex-col gap-2" data-testid="revision-rail">
      <span className="font-mono text-[10px] uppercase tracking-[0.09em] text-fg-secondary">
        Revision history
      </span>
      <div ref={trackRef} className="flex items-stretch gap-0 overflow-x-auto pb-1">
        {entries.map((e, i) => (
          <div key={e.revision} className="flex shrink-0 items-center">
            {i > 0 && (
              <span aria-hidden="true" className="mx-1 h-px w-6 shrink-0 border-t border-dashed border-bg-border" />
            )}
            <div
              data-current={e.isCurrent ? "true" : undefined}
              className={`flex w-[232px] shrink-0 items-start gap-2 rounded-lg border px-3 py-2 ${
                e.isCurrent
                  ? "border-accent-action/50 bg-bg-softer"
                  : "border-bg-border bg-bg-soft"
              }`}
            >
              <span
                className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${
                  e.isCurrent ? "bg-accent-action" : "bg-fg-secondary/50"
                }`}
              />
              <span className="flex min-w-0 flex-col">
                <span className="truncate font-mono text-[10px] uppercase tracking-[0.09em] text-fg-primary">
                  {e.name}
                </span>
                <span className="truncate text-[10px] text-fg-secondary">{e.when}</span>
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
